import React, { useCallback, useRef, useState } from 'react';
import { ArrowUpRight, Check, X } from 'lucide-react';
import { joinTeamOptions } from '../mock';
import { useToast } from '../hooks/use-toast';
import useReveal from '../hooks/useReveal';
import axios from 'axios';
import Turnstile, { HoneypotField, HONEYPOT_FIELD } from './Turnstile';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const inputClass =
  'mt-2 w-full bg-transparent border-b border-white/15 focus:border-[color:var(--green)] py-3 text-[15px] outline-none transition-colors text-[color:var(--parchment)] placeholder:text-[color:var(--parchment)]/25';

const renderField = (field, value, onChange) => {
  if (field.type === 'textarea') {
    return (
      <textarea
        rows={3}
        value={value}
        onChange={onChange}
        placeholder={field.placeholder}
        className={`${inputClass} resize-none`}
      />
    );
  }
  if (field.type === 'select') {
    return (
      <select value={value} onChange={onChange} className={inputClass}>
        <option value="" className="bg-[color:var(--obsidian)]">Select an option …</option>
        {field.options.map((o) => (
          <option key={o} value={o} className="bg-[color:var(--obsidian)]">{o}</option>
        ))}
      </select>
    );
  }
  return (
    <input
      type={field.type}
      value={value}
      onChange={onChange}
      placeholder={field.placeholder}
      className={inputClass}
    />
  );
};

const isComplete = (option, data) =>
  option.fields.every((f) => (f.required ? !!data[f.name] : true));

const ApplyDialog = ({ option, open, onClose }) => {
  const [data, setData] = useState({});
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState('');
  const [hp, setHp] = useState('');
  const turnstileRef = useRef(null);
  const setTokenStable = useCallback((v) => setToken(v), []);
  const { toast } = useToast();

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!isComplete(option, data)) {
      toast({ title: 'A required field is empty', description: 'Please complete the required entries to continue.' });
      return;
    }
    if (!token) {
      toast({ title: 'Security check needed', description: 'Please complete the security check before submitting.' });
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/api/forms/careers`, {
        ...data,
        [HONEYPOT_FIELD]: hp,
        turnstile_token: token,
      });
      setDone(true);
      toast({ title: 'Received', description: 'A specialist will follow up within one business day.' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast({
        title: 'Submission failed',
        description: typeof detail === 'string' ? detail : 'Please try again in a moment or call us directly.',
      });
    } finally {
      turnstileRef.current?.reset();
      setToken('');
      setBusy(false);
    }
  };

  const handleClose = () => {
    setData({});
    setDone(false);
    setToken('');
    setHp('');
    turnstileRef.current?.reset();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <div onClick={handleClose} className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative w-full max-w-lg bg-[color:var(--obsidian-2)] border border-white/10 rounded-[10px] shadow-2xl shadow-black/60 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6 md:p-8 border-b border-white/10">
          <div>
            <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)]">{option.kicker}</div>
            <div className="font-display text-[22px] md:text-[26px] text-[color:var(--parchment)] mt-1 leading-tight">{option.formTitle}</div>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-[color:var(--parchment)] hover:border-[color:var(--green)] hover:text-[color:var(--green)] transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {done ? (
          <div className="p-6 md:p-8">
            <div className="w-12 h-12 rounded-full bg-[color:var(--green)] flex items-center justify-center mb-5">
              <Check className="w-5 h-5 text-[color:var(--obsidian)]" />
            </div>
            <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)]">Received</div>
            <div className="font-display text-[24px] md:text-[28px] text-[color:var(--parchment)] mt-2 leading-tight">
              Thanks, {(data.name || '').split(' ')[0] || 'friend'}.
            </div>
            <p className="mt-3 text-[color:var(--parchment)]/70 text-[14.5px] leading-relaxed">
              A specialist will follow up within one business day.
            </p>
            <button onClick={handleClose} className="mt-6 btn-outline">Close</button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 md:p-8">
            <p className="text-[color:var(--parchment)]/70 text-[14.5px] leading-relaxed mb-6">{option.formBlurb}</p>
            <div className="space-y-6">
              {option.fields.map((f) => (
                <div key={f.name}>
                  <label className="font-mono text-[12px] md:text-[10.5px] tracking-[0.22em] uppercase text-[color:var(--parchment)]/60">
                    {f.label}{f.required ? '*' : ''}
                  </label>
                  {renderField(f, data[f.name] || '', (e) => setData({ ...data, [f.name]: e.target.value }))}
                </div>
              ))}
            </div>
            <HoneypotField value={hp} onChange={setHp} />
            <div className="mt-6">
              <Turnstile ref={turnstileRef} onToken={setTokenStable} theme="dark" />
            </div>
            <div className="mt-8 flex items-center justify-between">
              <button type="button" onClick={handleClose} className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-[color:var(--parchment)]/70 hover:text-[color:var(--green)] transition-colors">
                Cancel
              </button>
              <button type="submit" disabled={busy || !token} className={`btn-primary ${(busy || !token) ? 'opacity-60 cursor-not-allowed' : ''}`} data-testid="careers-submit-btn">
                {busy ? 'Submitting…' : 'Submit'} <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

const OptionCard = ({ option, onOpen, index }) => (
  <article
    className={`reveal pillar-col group relative overflow-hidden p-8 md:p-10 transition-colors duration-500 ${index === 0 ? 'md:pl-0' : 'md:pr-0'}`}
    style={{ transitionDelay: `${index * 100}ms` }}
  >
    {/* Team photo background */}
    <div aria-hidden="true" className="pointer-events-none absolute inset-0">
      <img
        src="/careers-team.v2.webp" srcSet="/careers-team.v2-400w.webp 400w, /careers-team.v2-800w.webp 800w, /careers-team.v2.webp 2000w" sizes="(max-width: 640px) 400px, (max-width: 1024px) 800px, 2000px" loading="lazy" decoding="async"
        alt=""
        className="w-full h-full object-cover object-center opacity-70"
      />
      <div className="absolute inset-0 bg-[color:var(--obsidian)]/75" />
      <div className="absolute inset-0 bg-gradient-to-b from-[color:var(--obsidian)]/60 via-transparent to-[color:var(--obsidian)]/80" />
    </div>

    {/* Two independent mobile-only translucent glass bubbles — one hugs
        the description paragraph, another hugs the benefits checklist.
        Both reset to plain wrappers on md+ so the desktop layout is
        untouched. The outer .relative here is only a stacking context. */}
    <div className="relative">
    <div className="flex items-center justify-between mb-8">
      <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--parchment)]/45">{option.number}</div>
      <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)]">{option.kicker}</div>
    </div>

    <h3 className="font-display text-[30px] md:text-[42px] leading-[1.05] text-[color:var(--parchment)]">
      {option.title}
    </h3>
    <p className="mt-5 max-w-md text-[color:var(--parchment)]/70 text-[15px] leading-relaxed mobile-glass-panel bg-black/25 backdrop-blur-[6px] rounded-xl px-3.5 py-3 md:bg-transparent md:backdrop-blur-none md:rounded-none md:px-0 md:py-0">{option.body}</p>

    <ul className="mt-6 md:mt-8 space-y-3 mobile-glass-panel bg-black/25 backdrop-blur-[6px] rounded-xl px-3.5 py-3 md:bg-transparent md:backdrop-blur-none md:rounded-none md:px-0 md:py-0">
      {option.benefits.map((b) => (
        <li key={b} className="flex items-start gap-3 text-[color:var(--parchment)]/80 text-[14.5px]">
          <span className="mt-1 w-5 h-5 rounded-full bg-[color:var(--green)]/15 border border-[color:var(--green)]/40 flex items-center justify-center shrink-0">
            <Check className="w-3 h-3 text-[color:var(--green)]" />
          </span>
          <span>{b}</span>
        </li>
      ))}
    </ul>

    <button onClick={() => onOpen(option)} className="mt-10 btn-primary">
      {option.cta} <ArrowUpRight className="w-3.5 h-3.5" />
    </button>
    </div>
  </article>
);

const JoinTeam = () => {
  const ref = useReveal({ threshold: 0.08 });
  const [active, setActive] = useState(null);

  return (
    <section id="join" ref={ref} className="relative py-16 md:py-20 px-6 md:px-10 border-t border-white/5 overflow-hidden">
      <div className="pointer-events-none absolute -right-24 top-40 w-[420px] h-[420px] rounded-full bg-[color:var(--green)]/[0.08] blur-3xl animate-floaty-slow" />

      <div className="relative max-w-[1500px] mx-auto">
        <div className="grid md:grid-cols-[.55fr_.45fr] gap-10 items-end mb-14">
          <div>
            <div className="reveal font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-4">
              &mdash; Join Our Team
            </div>
            <h2 className="reveal font-display text-[38px] md:text-[64px] leading-[1.02] text-[color:var(--parchment)]">
              Build your career <span className="text-[color:var(--green)]">with Ionic</span>.
            </h2>
          </div>
          <p className="reveal text-[color:var(--parchment)]/70 leading-relaxed max-w-md md:justify-self-end">
            Grow with a team of estate professionals delivering exceptional service on every project, backed by proven systems and thirty-plus years of experience.
          </p>
        </div>

        {/* Brand banner — misty PNW backdrop with the Ionic mark */}
        <figure className="reveal relative mb-14 md:mb-16 rounded-2xl overflow-hidden border border-white/10">
          <div className="relative aspect-[4/5] sm:aspect-[16/9] md:aspect-[21/9]">
            <img
              src="/join-brand.webp" srcSet="/join-brand-400w.webp 400w, /join-brand-800w.webp 800w, /join-brand.webp 2000w" sizes="(max-width: 640px) 400px, (max-width: 1024px) 800px, 2000px" loading="lazy" decoding="async"
              alt="Ionic Estate Sales brand mark set against a misty Pacific Northwest forest"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Light Artifact Aperture treatment — brand image should read */}
            <div className="absolute inset-0 bg-[color:var(--obsidian)]/10" />
            <div className="absolute inset-0 bg-gradient-to-t from-[color:var(--obsidian)] via-transparent to-transparent" />

            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-10">
              <div className="flex items-end justify-between gap-6 flex-wrap">
                <div className="max-w-xl">
                  <div className="font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--green)] mb-2">
                    &mdash; Where the work was founded.
                  </div>
                  <p className="font-display text-[15px] sm:text-[18px] md:text-[26px] leading-[1.2] text-[color:var(--parchment)]">
                    Rooted in the Pacific Northwest, built on <span className="text-[color:var(--green)]">integrity, craft, and thirty-plus years of trust</span>.
                  </p>
                </div>
                <div className="hidden md:block font-mono text-[12px] md:text-[10.5px] tracking-[0.24em] uppercase text-[color:var(--parchment)]/60">
                  Seattle · Est. 1994
                </div>
              </div>
            </div>
          </div>
        </figure>

        <div className="grid md:grid-cols-1 gap-0 border-t border-white/10">
          {joinTeamOptions.map((o, i) => (
            <OptionCard key={o.id} option={o} index={i} onOpen={setActive} />
          ))}
        </div>
      </div>

      <ApplyDialog option={active} open={!!active} onClose={() => setActive(null)} />
    </section>
  );
};

export default JoinTeam;
